import React, { useState, useEffect, useMemo } from 'react';
import './TradeScorecard.css';

function PnlBar({ pct }) {
  const capped = Math.max(-20, Math.min(20, pct));
  const width = Math.abs(capped) * 5; // 0–100%
  return (
    <div className="pnl-bar">
      <div className="pnl-bar__track">
        <div
          className={`pnl-bar__fill ${capped >= 0 ? 'up' : 'down'}`}
          style={{ width: `${width}%`, marginLeft: capped < 0 ? `${50 - width}%` : '50%' }}
        />
        <div className="pnl-bar__center" />
      </div>
    </div>
  );
}

function PositionCard({ pos }) {
  const isUp = pos.pnl_pct >= 0;
  const sign = isUp ? '+' : '';
  return (
    <div className={`position-card ${isUp ? 'position-card--up' : 'position-card--down'}`}>
      <div className="position-card__header">
        <span className="position-card__ticker">{pos.ticker}</span>
        <span className={`position-card__dir ${pos.direction}`}>{pos.direction.toUpperCase()}</span>
        <span className={`position-card__pnl-pct ${isUp ? 'up' : 'down'}`}>
          {sign}{pos.pnl_pct.toFixed(1)}%
        </span>
      </div>
      <div className="position-card__trade-name">{pos.trade_name}</div>
      <div className="position-card__prices">
        <span className="position-card__label">Entry</span>
        <span className="position-card__price">${pos.entry_price?.toFixed(2)}</span>
        <span className="position-card__arrow">→</span>
        <span className="position-card__label">Now</span>
        <span className={`position-card__price ${isUp ? 'up' : 'down'}`}>
          ${pos.current_price?.toFixed(2)}
        </span>
      </div>
      <PnlBar pct={pos.pnl_pct} />
      <div className={`position-card__dollar ${isUp ? 'up' : 'down'}`}>
        {sign}${Math.abs(pos.pnl_dollars).toFixed(0)}
        <span className="position-card__days">{pos.days_held}d</span>
      </div>
    </div>
  );
}

export default function TradeScorecard() {
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/portfolio')
      .then(r => r.json())
      .then(data => { setPortfolio(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const { byThesis, summary } = useMemo(() => {
    if (!portfolio?.positions?.length) return { byThesis: [], summary: null };

    // Calculate P&L per position
    const positions = portfolio.positions.map(pos => {
      const entry = pos.entry_price || 0;
      const current = pos.price_history
        ? Object.values(pos.price_history).slice(-1)[0]
        : entry;
      const pnl_pct = entry
        ? pos.direction === 'long'
          ? (current - entry) / entry * 100
          : (entry - current) / entry * 100
        : 0;
      const pnl_dollars = pos.dollar_allocation * pnl_pct / 100;
      const days_held = pos.entry_date
        ? Math.floor((Date.now() - new Date(pos.entry_date)) / 86400000)
        : 0;
      return { ...pos, current_price: current, pnl_pct, pnl_dollars, days_held };
    });

    // Group by thesis date
    const groups = {};
    positions.forEach(p => {
      const key = p.thesis_date;
      if (!groups[key]) groups[key] = [];
      groups[key].push(p);
    });

    const byThesis = Object.entries(groups)
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([date, pos]) => {
        const runPnl = pos.reduce((s, p) => s + p.pnl_dollars, 0);
        const runInvested = pos.reduce((s, p) => s + (p.dollar_allocation || 0), 0);
        const runPct = runInvested ? runPnl / runInvested * 100 : 0;
        const wins = pos.filter(p => p.pnl_dollars > 0).length;
        return { date, positions: pos, runPnl, runPct, wins };
      });

    const totalInvested = positions.reduce((s, p) => s + (p.dollar_allocation || 0), 0);
    const totalPnl = positions.reduce((s, p) => s + p.pnl_dollars, 0);
    const wins = positions.filter(p => p.pnl_dollars > 0).length;

    const summary = {
      totalInvested,
      totalPnl,
      totalPct: totalInvested ? totalPnl / totalInvested * 100 : 0,
      positions: positions.length,
      wins,
      winRate: positions.length ? (wins / positions.length * 100).toFixed(0) : 0,
      bankroll: portfolio.bankroll || 100000,
    };

    return { byThesis, summary };
  }, [portfolio]);

  if (loading) return <div className="scorecard-loading">Loading portfolio...</div>;
  if (!portfolio?.positions?.length) return null;

  const totalSign = summary.totalPnl >= 0 ? '+' : '';

  return (
    <section className="trade-scorecard">
      <div className="trade-scorecard__header">
        <h2 className="trade-scorecard__title">Trade Scorecard</h2>
        <div className="trade-scorecard__summary">
          <div className="scorecard-stat">
            <span className="scorecard-stat__label">Simulated P&amp;L</span>
            <span className={`scorecard-stat__value ${summary.totalPnl >= 0 ? 'up' : 'down'}`}>
              {totalSign}${Math.abs(summary.totalPnl).toFixed(0)}
              <span className="scorecard-stat__pct"> ({totalSign}{summary.totalPct.toFixed(1)}%)</span>
            </span>
          </div>
          <div className="scorecard-stat">
            <span className="scorecard-stat__label">Win Rate</span>
            <span className="scorecard-stat__value">{summary.winRate}%</span>
          </div>
          <div className="scorecard-stat">
            <span className="scorecard-stat__label">Positions</span>
            <span className="scorecard-stat__value">{summary.wins}/{summary.positions}</span>
          </div>
          <div className="scorecard-stat">
            <span className="scorecard-stat__label">Bankroll</span>
            <span className="scorecard-stat__value">${(summary.bankroll / 1000).toFixed(0)}k sim</span>
          </div>
        </div>
        <p className="trade-scorecard__disclaimer">
          Simulated positions only — tracking thesis accuracy, not financial advice.
        </p>
      </div>

      {byThesis.map(({ date, positions, runPnl, runPct, wins }) => {
        const sign = runPnl >= 0 ? '+' : '';
        return (
          <div key={date} className="thesis-run">
            <div className="thesis-run__header">
              <span className="thesis-run__date">Thesis {date}</span>
              <span className={`thesis-run__pnl ${runPnl >= 0 ? 'up' : 'down'}`}>
                {sign}${Math.abs(runPnl).toFixed(0)} ({sign}{runPct.toFixed(1)}%)
              </span>
              <span className="thesis-run__wins">{wins}/{positions.length} winning</span>
            </div>
            <div className="thesis-run__positions">
              {[...positions]
                .sort((a, b) => b.pnl_dollars - a.pnl_dollars)
                .map(pos => <PositionCard key={pos.position_key} pos={pos} />)
              }
            </div>
          </div>
        );
      })}
    </section>
  );
}
